import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Phone, Video, Users, Clock } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { ActiveCall } from "../../services/callManagementService";

interface CallInProgressBannerProps {
  activeCall: ActiveCall;
  currentUserId: string;
  onJoinCall: () => void;
  onDismiss?: () => void;
  className?: string;
  showDismissButton?: boolean;
}

export const CallInProgressBanner: React.FC<CallInProgressBannerProps> = ({
  activeCall,
  currentUserId,
  onJoinCall,
  onDismiss,
  className = "",
  showDismissButton = true
}) => {
  console.log("activeCall", activeCall);
  const isCurrentUserInCall = activeCall.participants.some(
    (participant) => participant.userId === currentUserId && participant.status !== "left" && participant.status !== "invited"
  );

  const [currentDuration, setCurrentDuration] = useState<string>("00:00");
  const [activeParticipants, setActiveParticipants] = useState(
    activeCall.participants.filter((participant) => participant.status === "connected")
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedSound = useRef<boolean>(false);

  // Real-time timer effect
  useEffect(() => {
    if (!activeCall.startedAt && !activeCall.createdAt) return;

    const startTime = activeCall.startedAt || activeCall.createdAt;
    const start = new Date(startTime).getTime();

    const updateTimer = () => {
      const now = Date.now();
      const duration = Math.floor((now - start) / 1000);

      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;

      setCurrentDuration(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimer(); // Update immediately
    const interval = setInterval(updateTimer, 1000); // Update every second

    return () => clearInterval(interval);
  }, [activeCall.startedAt, activeCall.createdAt]);

  // Update participants when activeCall changes
  useEffect(() => {
    setActiveParticipants(
      activeCall.participants.filter((participant) => participant.status === "connected")
    );
  }, [activeCall.participants]);

  // Play notification sound when banner appears
  useEffect(() => {
    if (!hasPlayedSound.current) {
      // Create a simple notification sound using Web Audio API
      const playNotificationSound = () => {
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

          // Create a pleasant notification tone
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // Set frequency for a pleasant tone (C5 note)
          oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
          oscillator.type = "sine";

          // Set volume envelope
          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

          // Play the sound
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.5);

          hasPlayedSound.current = true;
        } catch (error) {
          console.log("Could not play notification sound:", error);
        }
      };

      playNotificationSound();
    }
  }, []);

  // Reset sound flag when call changes
  useEffect(() => {
    hasPlayedSound.current = false;
  }, [activeCall.id]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 rounded-lg shadow-lg ${className}`}
    >
      {/* Mobile Layout */}
      <div className="block sm:hidden">
        {/* Top Row - Call Info and Dismiss */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Call Type Icon */}
            <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-full flex-shrink-0">
              {activeCall.callType === "video" ? (
                <Video className="w-4 h-4" />
              ) : (
                <Phone className="w-4 h-4" />
              )}
            </div>

            {/* Call Details */}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate">
                  {activeCall.callType === "video" ? "Video Call" : "Audio Call"}
                </span>
                <Badge variant="secondary" className="bg-white bg-opacity-20 text-white border-none text-xs">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse mr-1"></div>
                  Live
                </Badge>
              </div>
              <div className="text-xs opacity-90 truncate">
                Started by {activeCall.initiator.firstName || activeCall.initiator.name}
              </div>
            </div>
          </div>

          {/* Dismiss Button */}
          {onDismiss && showDismissButton && (
            <Button
              onClick={onDismiss}
              variant="ghost"
              className="text-white hover:bg-white hover:bg-opacity-20 p-1 flex-shrink-0"
              title="Don't show again for this call"
            >
              ×
            </Button>
          )}
        </div>

        {/* Middle Row - Stats */}
        <div className="flex items-center justify-between mb-3 text-xs">
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span>{activeParticipants.length} participant{activeParticipants.length !== 1 ? "s" : ""}</span>
          </div>
          {(activeCall.startedAt || activeCall.createdAt) && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span className="font-mono">{currentDuration}</span>
            </div>
          )}
        </div>

        {/* Bottom Row - Avatars and Join Button */}
        <div className="flex items-center justify-between">
          {/* Participant Avatars */}
          <div className="flex -space-x-1">
            {activeParticipants.slice(0, 2).map((participant) => (
              <div
                key={participant.id}
                className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center border border-white"
                title={participant?.user?.firstName || participant?.user?.name}
              >
                <span className="text-xs font-medium">
                  {(participant?.user?.firstName || participant?.user?.name || "?").charAt(0).toUpperCase()}
                </span>
              </div>
            ))}
            {activeParticipants.length > 2 && (
              <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center border border-white">
                <span className="text-xs font-medium">+{activeParticipants.length - 2}</span>
              </div>
            )}
          </div>

          {/* Join/Rejoin Button */}
          {!isCurrentUserInCall ? (
            <Button
              onClick={onJoinCall}
              variant="outline"
              className="border-white !text-blue-600 hover:bg-white hover:text-blue-600 font-medium px-3 py-1.5 text-sm"
            >
              Join
            </Button>
          ) : (
            <Button
              onClick={onJoinCall}
              variant="outline"
              className="border-white !text-blue-600 hover:bg-white hover:text-blue-600 font-medium px-3 py-1.5 text-sm"
            >
              Rejoin
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center justify-between">
        {/* Call Info */}
        <div className="flex items-center gap-3">
          {/* Call Type Icon */}
          <div className="flex items-center justify-center w-10 h-10 bg-white bg-opacity-20 rounded-full">
            {activeCall.callType === "video" ? (
              <Video className="w-5 h-5" />
            ) : (
              <Phone className="w-5 h-5" />
            )}
          </div>

          {/* Call Details */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-semibold">
                {activeCall.callType === "video" ? "Video Call" : "Audio Call"} in Progress
              </span>
              <Badge variant="secondary" className="bg-white bg-opacity-20 text-white border-none">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-1"></div>
                Live
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-sm opacity-90">
              {/* Participants */}
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{activeParticipants.length} participant{activeParticipants.length !== 1 ? "s" : ""}</span>
              </div>

              {/* Duration */}
              {(activeCall.startedAt || activeCall.createdAt) && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-mono">{currentDuration}</span>
                </div>
              )}

              {/* Initiator */}
              <div className="text-sm">
                Started by {activeCall.initiator.firstName || activeCall.initiator.name}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Participant Avatars */}
          <div className="flex -space-x-2 mr-3">
            {activeParticipants.slice(0, 3).map((participant) => (
              <div
                key={participant.id}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-2 border-white"
                title={participant?.user?.firstName || participant?.user?.name}
              >
                <span className="text-xs font-medium">
                  {(participant?.user?.firstName || participant?.user?.name || "?").charAt(0).toUpperCase()}
                </span>
              </div>
            ))}
            {activeParticipants.length > 3 && (
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-2 border-white">
                <span className="text-xs font-medium">+{activeParticipants.length - 3}</span>
              </div>
            )}
          </div>

          {/* Join/Rejoin Button */}
          {!isCurrentUserInCall ? (
            <Button
              onClick={onJoinCall}
              variant="outline"
              className="border-white !text-blue-600 hover:bg-white hover:text-blue-600 font-medium px-4 py-2"
            >
              Join Call
            </Button>
          ) : (
            <Button
              onClick={onJoinCall}
              variant="outline"
              className="border-white !text-blue-600 hover:bg-white hover:text-blue-600 font-medium px-4 py-2"
            >
              Rejoin Call
            </Button>
          )}

          {/* Dismiss Button */}
          {onDismiss && showDismissButton && (
            <Button
              onClick={onDismiss}
              variant="ghost"
              className="text-white hover:bg-white hover:bg-opacity-20 p-2"
              title="Don't show again for this call"
            >
              ×
            </Button>
          )}
        </div>
      </div>

      {/* Participant List (collapsed view) - Desktop only */}
      {activeParticipants.length > 0 && (
        <div className="hidden sm:block mt-3 pt-3 border-t border-white border-opacity-20">
          <div className="flex flex-wrap gap-2">
            {activeParticipants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center gap-2 bg-white bg-opacity-10 rounded-full px-3 py-1 text-sm"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>{participant.user?.firstName || participant.user?.name || "Unknown User"}</span>

                {/* Audio/Video Status */}
                <div className="flex items-center gap-1">
                  {participant.isAudioEnabled === false && (
                    <div className="w-3 h-3 bg-red-400 rounded-full" title="Muted" />
                  )}
                  {participant.isVideoEnabled === false && activeCall.callType === "video" && (
                    <div className="w-3 h-3 bg-yellow-400 rounded-full" title="Video off" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};
